import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import dayjs from "dayjs";
import Utc from "dayjs/plugin/utc";
import confetti from "canvas-confetti";
import { useTitle } from "@/lib/common/useTitle";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { HeaderBarSelector } from "@/features/common/components/HeaderBarSelector";
import { EmblemStats } from "@/features/emblems/components/EmblemStats";
import { ModalEmblemsRoll } from "@/features/emblems/components/ModalEmblemsRoll";
import { ModalEmblemsExplain } from "@/features/emblems/components/ModalEmblemsExplain";
import { Emblem, getGoldenThresholdForEmblemRarity } from "@/features/common/components/Emblem";
import { getColorForEmblemRarity } from "@/features/emblems/getColorForEmblemRarity";
import { useEmblems } from "@/features/emblems/api/useEmblems";
import { useEmblemRolls } from "@/features/emblems/api/useEmblemRolls";
import { useEmblem } from "@/features/common/api/useEmblem";
import { useSetUserEmblem } from "@/features/emblems/api/useSetUserEmblem";
import { useSpecialEmblemStatus } from "@/features/emblems/api/useSpecialEmblemStatus";
import { useUnlockSpecialEmblem } from "@/features/emblems/api/useUnlockSpecialEmblem";
import { useStorage } from "@/lib/storage";
import { LoadingState } from "@/ds/custom/LoadingState";
import { SectionHeader } from "@/ds/custom/SectionHeader";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { cn } from "@/ds/utils";
import type { EmblemModel } from "@/features/emblems/api/models/EmblemModel";
import type { EmblemRarity } from "@/lib/domain/EmblemRarity";
import { EmblemSetOptions } from "@/lib/domain/EmblemSet";
import { CheckIcon, LockIcon, LockOpenIcon } from "lucide-react";

dayjs.extend(Utc);

const RARITY_ORDER: EmblemRarity[] = ["Fabled", "Legendary", "Majestic", "Rare", "Common"];

export const PageEmblems: FC = () => {
	useTitle("Emblems");

	const { state, dispatchSetEmblemSet } = useUserPreferencesState();
	const emblems = useEmblems(state.emblemSet);
	const setUserEmblem = useSetUserEmblem();
	const storage = useStorage();

	const [rollModalVisible, setRollModalVisible] = useState(false);
	const [explainModalOpen, setExplainModalOpen] = useState(false);

	useNotifyMutationSuccess(setUserEmblem, "Emblem Equipped");

	const groupedEmblems = useMemo(() => {
		if (!emblems.data) {
			return {} as Record<string, EmblemModel[]>;
		}

		return emblems.data.pages
			.flatMap(x => x.results)
			.reduce((acc, emblem) => {
				if (!acc[emblem.rarity]) {
					acc[emblem.rarity] = [];
				}
				acc[emblem.rarity].push(emblem);
				return acc;
			}, {} as Record<string, EmblemModel[]>);
	}, [emblems.data]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			const hasDisplayed = storage.getPersistent<boolean>("hasDisplayed2026EmblemSetModal");
			if (hasDisplayed || rollModalVisible) {
				return;
			}
			setExplainModalOpen(true);
		}, 1500);

		return () => clearTimeout(timeout);
	}, [rollModalVisible]);

	return (
		<>
			<HeaderBar
				title="Emblems"
				description="Collect emblems to personalize your experience!"
				breadcrumb={[
					{ label: "Dashboard", href: "/dashboard" },
					{ label: "Emblems" }
				]}
			/>

			<HeaderBarTools>
				{
					state.emblemSet !== "SpecialEmblems" &&
					<EmblemStats set={state.emblemSet} />
				}

				<HeaderBarSelector
					placeholder="Emblem Set"
					value={state.emblemSet}
					onChange={dispatchSetEmblemSet}
					options={EmblemSetOptions}
				/>
			</HeaderBarTools>

			{
				state.emblemSet !== "SpecialEmblems" &&
				<EmblemRollControls
					rollModalVisible={rollModalVisible}
					onRoll={() => setRollModalVisible(true)} />
			}

			<div>
				{
					(emblems.isError || setUserEmblem.isError) && (
						<div className="px-4 pt-4 flex flex-col gap-2">
							{emblems.isError && <ErrorDisplay error={emblems.error} />}
							{setUserEmblem.isError && <ErrorDisplay error={setUserEmblem.error} />}
						</div>
					)
				}

				{emblems.isLoading && <LoadingState />}

				{
					RARITY_ORDER.map(rarity => (
						groupedEmblems[rarity] && groupedEmblems[rarity].length > 0 && (
							<EmblemRaritySection
								key={rarity}
								rarity={rarity}
								emblems={groupedEmblems[rarity]}
								onSelect={emblemId => setUserEmblem.mutate({ emblemId })}
							/>
						)
					))
				}

				{
					state.emblemSet === "SpecialEmblems" &&
					<EmblemSpecialSection emblems={groupedEmblems["Special"] || []} onSelect={emblemId => setUserEmblem.mutate({ emblemId })} />
				}
			</div>

			<ModalEmblemsRoll
				set={state.emblemSet}
				visible={rollModalVisible}
				onCancel={() => setRollModalVisible(false)}
			/>

			<ModalEmblemsExplain
				open={explainModalOpen}
				onOpenChange={setExplainModalOpen}
			/>
		</>
	);
};

interface EmblemRollControlsProps {
	rollModalVisible: boolean;
	onRoll: () => void;
}

const EmblemRollControls: FC<EmblemRollControlsProps> = props => {
	const {
		rollModalVisible,
		onRoll
	} = props;

	const rolls = useEmblemRolls();
	const [currentTime, setCurrentTime] = useState(new Date());

	useEffect(() => {
		const interval = setInterval(() => setCurrentTime(new Date()), 1000);
		return () => clearInterval(interval);
	}, []);

	if (rolls.isError) {
		return (
			<div className="border-b p-3">
				<ErrorDisplay error={rolls.error} />
			</div>
		);
	}

	if (!rolls.data) {
		return null;
	}

	const hasRolls = rolls.data.emblemRolls > 0;

	return (
		<div className={cn(
			"flex flex-col @md:flex-row @md:items-center @md:justify-between border-b px-4 py-4 gap-4 transition-colors duration-500",
			hasRolls && "bg-primary/5"
		)}>
			<div className="flex items-center justify-center gap-4 @md:justify-start">
				<span
					className={cn(
						"text-5xl font-bold tabular-nums leading-none transition-colors duration-500",
						hasRolls ? "text-primary" : "text-muted-foreground"
					)}
					style={hasRolls ? { animation: "emblem-roll-glow 1.5s ease-in-out infinite" } : undefined}>
					{rolls.data.emblemRolls}
				</span>
				<div className="flex flex-col gap-0.5">
					<span className={cn("type-section", !hasRolls && "text-muted-foreground")}>
						Rolls Remaining
					</span>
					<span className="type-meta">
						Resets in {getDurationText(currentTime, getNextUTCMidnight())}
					</span>
				</div>
			</div>
			<Button
				size="default"
				className="w-full @md:w-40 shrink-0"
				disabled={!hasRolls || rollModalVisible}
				onClick={onRoll}>
				Roll
			</Button>
		</div>
	);
};

interface EmblemRaritySectionProps {
	rarity: EmblemRarity;
	emblems: EmblemModel[];
	onSelect: (emblemId: string) => void;
}

const EmblemRaritySection: FC<EmblemRaritySectionProps> = props => {
	const {
		rarity,
		emblems,
		onSelect
	} = props;

	return (
		<div className="border-b">
			<SectionHeader
				title={rarity}
				leading={<div className="size-2.5 rounded-full shrink-0" style={{ backgroundColor: getColorForEmblemRarity(rarity) }} />} />
			<div className="grid gap-3 px-4 py-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
				{
					emblems.map(emblem => (
						<div key={emblem.emblemId} className="flex flex-col gap-1">
							<div className={emblem.count > 0 ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}>
								<Emblem
									{...emblem}
									onClick={emblem.count > 0 ? () => onSelect(emblem.emblemId) : undefined}
								/>
							</div>
							<div className="text-xs text-center truncate">{emblem.name}</div>
							<div className="type-meta text-center">
								{emblem.count}/{getGoldenThresholdForEmblemRarity(emblem.rarity)}
							</div>
						</div>
					))
				}
			</div>
		</div>
	);
};

type EmblemSpecialSectionProps = Omit<EmblemRaritySectionProps, "rarity">;

const EmblemSpecialSection: FC<EmblemSpecialSectionProps> = props => {
	const {
		emblems,
		onSelect
	} = props;

	return (
		<div className="grid gap-4 p-8" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(192px, 256px))" }}>
			{
				emblems.map(emblem => (
					<SpecialEmblemDisplay key={emblem.emblemId} emblem={emblem} onSelect={onSelect} />
				))
			}
		</div>
	);
};

interface SpecialEmblemDisplayProps {
	emblem: EmblemModel;
	onSelect: (emblemId: string) => void;
}

const SpecialEmblemDisplay: FC<SpecialEmblemDisplayProps> = props => {
	const {
		emblem,
		onSelect
	} = props;

	const specialEmblemStatus = useSpecialEmblemStatus(emblem.emblemId);
	const activeEmblem = useEmblem();
	const unlockSpecialEmblem = useUnlockSpecialEmblem();
	const cardRef = useRef<HTMLDivElement>(null);

	useNotifyMutationSuccess(unlockSpecialEmblem, "Emblem Unlocked");

	const isUnlocked = specialEmblemStatus.data?.status === "Unlocked";
	const isUnlockable = specialEmblemStatus.data?.status === "Unlockable";
	const isEquipped = activeEmblem.data?.emblemId === emblem.emblemId;

	useEffect(() => {
		if (!unlockSpecialEmblem.isSuccess) {
			return;
		}

		const rect = cardRef.current?.getBoundingClientRect();
		const origin = rect
			? { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + rect.height / 2) / window.innerHeight }
			: { y: 0.5 };

		confetti({
			particleCount: 2400,
			spread: 180,
			origin,
			colors: getColorsForSpecialEmblemId(emblem.emblemId)
		});
	}, [unlockSpecialEmblem.isSuccess]);

	const onButtonClick = useCallback(() => {
		if (isUnlocked) {
			onSelect(emblem.emblemId);
			return;
		}

		if (isUnlockable) {
			unlockSpecialEmblem.mutate(emblem.emblemId);
		}
	}, [isUnlocked, isUnlockable, onSelect, emblem.emblemId, unlockSpecialEmblem]);

	if (!specialEmblemStatus.data) {
		return <LoadingState />;
	}

	return (
		<div ref={cardRef} className="flex flex-col gap-1 border p-4 @md:p-8 rounded-2xl">
			<div className={isUnlocked ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}>
				<Emblem className="w-full" {...emblem} onClick={isUnlocked ? onButtonClick : undefined} />
			</div>
			<div className="type-title text-center truncate">{emblem.name}</div>
			<div className="type-meta text-center">
				{emblem.description}
			</div>
			{
				unlockSpecialEmblem.isError &&
				<div className="mt-3">
					<ErrorDisplay error={unlockSpecialEmblem.error} />
				</div>
			}
			<Button
				variant="outline"
				disabled={(!isUnlocked && !isUnlockable) || unlockSpecialEmblem.isPending || isEquipped}
				className="mt-3"
				onClick={isUnlocked || isUnlockable ? onButtonClick : undefined}>
				{
					unlockSpecialEmblem.isPending
						? <Spinner />
						: isEquipped
							? (<><CheckIcon /> Equipped</>)
							: isUnlocked
								? "Equip"
								: (
									<>
										{isUnlockable ? <LockOpenIcon /> : <LockIcon />}
										{isUnlockable ? "Unlock" : "Not Unlocked"}
									</>
								)
				}
			</Button>
		</div>
	);
};

const getColorsForSpecialEmblemId = (specialEmblemId: string): string[] => {
	switch (specialEmblemId) {
	case "TAYLOR":
		return ["#2B2B2B", "#555555", "#FFD700", "#D4AF37"];
	case "PINK_VISOR":
		return ["#FF69B4", "#FF1493", "#FFB6C1", "#FFC0CB", "#DB7093"];
	case "POPPED_COLLAR_POLO":
		return ["#2B2B2B", "#555555", "#1E90FF", "#4169E1"];
	default:
		return ["#FFFFFF", "#CCCCCC", "#EEEEEE"];
	}
};

const getNextUTCMidnight = (): Date => {
	return dayjs(dayjs().utc()).add(1, "day").startOf("day").toDate();
};

const getDurationText = (now: Date, to: Date): string => {
	const duration = dayjs(to).diff(now, "second");
	const hours = Math.floor(duration / 3600);
	const minutes = Math.floor((duration % 3600) / 60);
	const seconds = duration % 60;

	return `${hours}h ${minutes}m ${seconds}s`;
};
